import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Loader2Icon, XIcon } from 'components/icons';
import { authorInitials, avatarColors } from '@common/audit-trail/auditTrailFormat';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useJoinedPanel } from 'layout/NewDesignLayout/joinedPanels';
import { useEffect, useRef, useState } from 'react';
import { ClassifierData } from 'types/classifiers';
import { EntityAuditTrailEntryDto } from 'types/entity-audit-trail';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { AuditCategoryFilter } from './AuditCategoryFilter';
import { AuditPersonFilter } from './AuditPersonFilter';
import { EntityAuditTrailDescriptor } from './entityAuditTrail';
import {
    AuditEntryGroup,
    bucketByLocalDay,
    eventSentenceSegments,
    isMonospacedValue,
    isUrlValue,
    localTimeLabel,
    mergeNote,
    utcTooltip,
    valueChange,
} from './entityAuditTrailFormat';
import { useEntityAuditTrailFeed } from './useEntityAuditTrailFeed';

const VALUE_TRUNCATE_AT = 64;
/** Beyond this a struck-through value reads as damaged text rather than as a superseded one. */
const STRIKETHROUGH_UP_TO = 24;

/**
 * The per-entity audit trail: a right-docked drawer with a human-readable, newest-first change
 * feed. It keeps the page behind interactive, with no backdrop, so a click anywhere outside the
 * drawer closes it; the portalled filter popovers still count as inside. Closed, it stays mounted,
 * slid out and hidden, so both opening and closing slide.
 * Timestamps render in the viewer's local time with the UTC instant in the tooltip; one author's
 * lines within a minute group under one card, reading chronologically inside it. `entityLabel` is
 * the human name of the entity for the subtitle (an invoice number, a case number). `detached` draws it as
 * the new design's box, under the top bar and off the window's edges, in place of the full-height dock. In
 * that design it is one of the side panels of which one is open at a time.
 */
export function EntityAuditTrailDrawer({
    trail,
    entityId,
    entityLabel,
    open,
    onClose,
    detached,
}: {
    trail: EntityAuditTrailDescriptor;
    entityId: number;
    entityLabel: string;
    open: boolean;
    onClose: () => void;
    detached?: boolean;
}) {
    useJoinedPanel(open, onClose);
    const feed = useEntityAuditTrailFeed(trail, entityId, open);
    const { classifiers } = useClassifiers({ codes: trail.classifierCodes });
    const dateFormat = useEffectiveDateFormat();
    const ns = trail.localeNamespace;

    // The click that opens the drawer still bubbles to the document after React has committed
    // `open`, so it would read as a click-away and close the drawer again. Arm on the next tick.
    const isClickAwayArmed = useRef(false);
    useEffect(() => {
        if (!open) {
            isClickAwayArmed.current = false;
            return undefined;
        }
        const timer = setTimeout(() => {
            isClickAwayArmed.current = true;
        }, 0);
        return () => clearTimeout(timer);
    }, [open]);

    // A click counts as inside when it lands in the panel or bubbles through it in the React tree,
    // which is how a click in a portalled filter popover arrives: the panel's own `onClick` runs
    // before the document listener and flags it.
    const panelRef = useRef<HTMLDivElement>(null);
    const isClickInside = useRef(false);
    useEffect(() => {
        const closeOnClickAway = (event: MouseEvent) => {
            const isInside = isClickInside.current || panelRef.current?.contains(event.target as Node);
            isClickInside.current = false;
            if (!isInside && isClickAwayArmed.current) {
                onClose();
            }
        };
        document.addEventListener('click', closeOnClickAway);
        return () => document.removeEventListener('click', closeOnClickAway);
    }, [onClose]);

    return (
        <div
            ref={panelRef}
            onClick={() => {
                isClickInside.current = true;
            }}
            className={cn(
                // Beside the AI panel docked to the side, which narrows the page by `--ai-side-dock-width`.
                'bg-paper border-divider fixed inset-y-0 right-[var(--ai-side-dock-width,0px)] z-(--z-drawer) w-[440px] overflow-y-auto border-l',
                // The box's top and gap are the new layout's, read off the document. The window less two
                // gaps caps the width. It moves as the new design's side panels do: it slides in 70% of its width out
                // of a fade and a blur, and fades out quicker into the blur while it moves 70% back; under reduced
                // motion it only fades, in 150ms.
                detached
                    ? 'shadow-panel top-(--panel-top) right-[calc(var(--panel-gap)+var(--ai-side-dock-width,0px))] bottom-(--panel-gap) max-w-[calc(100vw-2*var(--panel-gap)-var(--ai-side-dock-width,0px))] rounded-2xl border-0 outline-hidden transition-[translate,opacity,filter,visibility] duration-(--duration-slow) ease-(--ease-smooth-out) motion-reduce:transition-[opacity,visibility] motion-reduce:duration-150'
                    : 'shadow-[-8px_0_24px_rgba(15,23,42,0.06)] transition-[translate,visibility] duration-225 ease-out motion-reduce:transition-none',
                !open && !detached && 'invisible translate-x-[calc(100%+var(--ai-side-dock-width,0px))]',
                detached &&
                    !open &&
                    'invisible opacity-0 duration-(--duration-fast) motion-safe:translate-x-[70%] motion-safe:blur-(--blur-panel)'
            )}
        >
            <div className="flex h-full flex-col">
                <div className="flex items-start justify-between gap-2 px-6 pt-5 pb-3">
                    <div>
                        <div className="text-foreground text-base font-semibold">{t(`${ns}.title`)}</div>
                        <div className="text-muted-foreground mt-0.5 text-xs">
                            {feed.totalElements === undefined
                                ? entityLabel
                                : t(`${ns}.subtitle`, {
                                      label: entityLabel,
                                      count: String(feed.totalElements),
                                  })}
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        className="-mr-2"
                        aria-label={t(`${ns}.close`)}
                        onClick={onClose}
                    >
                        <XIcon className="size-4" />
                    </Button>
                </div>
                <div className="border-divider flex gap-2 border-b px-6 pt-1 pb-4">
                    <AuditCategoryFilter trail={trail} selected={feed.categories} onChange={feed.setCategories} />
                    <AuditPersonFilter
                        trail={trail}
                        entityId={entityId}
                        categories={feed.categories}
                        selected={feed.authorIds}
                        onChange={feed.setAuthorIds}
                        includeSystem={feed.includeSystem}
                        onIncludeSystemChange={feed.setIncludeSystem}
                    />
                </div>
                <div className="flex-1 [scrollbar-gutter:stable] overflow-y-auto pb-2">
                    {feed.isInitialLoading ? (
                        <div className="flex flex-col gap-3 p-6">
                            <Skeleton className="h-16" />
                            <Skeleton className="h-16" />
                            <Skeleton className="h-16" />
                        </div>
                    ) : feed.entries.length === 0 ? (
                        <div className="text-muted-foreground px-6 py-8 text-center text-sm">
                            {t(`${ns}.no-events`)}
                        </div>
                    ) : (
                        bucketByLocalDay(feed.entries, dateFormat).map((bucket) => (
                            <div key={bucket.label}>
                                <div className="border-divider bg-grey-50 text-grey-500 new:text-label new:tracking-[0.06em] sticky top-0 z-10 border-y px-6 py-1.5 text-[11.5px] font-semibold tracking-[0.05em] uppercase">
                                    {bucket.label}
                                </div>
                                {bucket.groups.map((group) => (
                                    <EntryGroupCard
                                        key={group.key}
                                        trail={trail}
                                        group={group}
                                        classifiers={classifiers}
                                    />
                                ))}
                            </div>
                        ))
                    )}
                </div>
                <div className="border-divider flex flex-col gap-2 border-t px-6 pt-3 pb-4">
                    {feed.hasMore && (
                        <Button
                            variant="outline"
                            className="w-full"
                            disabled={feed.isFetchingMore}
                            onClick={feed.loadMore}
                        >
                            {feed.isFetchingMore && <Loader2Icon className="size-3.5 animate-spin" />}
                            {t(`${ns}.load-older`)}
                        </Button>
                    )}
                    {feed.totalElements !== undefined && (
                        <div className="text-muted-foreground text-center text-xs">
                            {t(`${ns}.showing-of`, {
                                shown: String(feed.shownEventCount),
                                total: String(feed.totalElements),
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function EntryGroupCard({
    trail,
    group,
    classifiers,
}: {
    trail: EntityAuditTrailDescriptor;
    group: AuditEntryGroup;
    classifiers: Record<string, ClassifierData | undefined>;
}) {
    const authorShape = { author: group.author, changedBy: null, authorId: group.authorId };
    const colors = avatarColors(authorShape);
    return (
        <div className="hover:bg-grey-50 flex gap-3 px-6 py-3">
            <div
                className="flex size-8 flex-none items-center justify-center rounded-full text-xs font-semibold"
                style={{ backgroundColor: colors.background, color: colors.text }}
            >
                {authorInitials(authorShape)}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="text-foreground text-sm font-semibold">
                        {group.author ?? t(`${trail.localeNamespace}.system`)}
                    </span>
                    <span
                        className="text-muted-foreground text-xs whitespace-nowrap"
                        title={utcTooltip(group.changedAt)}
                    >
                        {localTimeLabel(group.changedAt)}
                    </span>
                </div>
                <div className="mt-1 flex flex-col gap-2">
                    {group.lines.map((line) => (
                        <EventLineRow key={line.entryId} trail={trail} line={line} classifiers={classifiers} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function EventLineRow({
    trail,
    line,
    classifiers,
}: {
    trail: EntityAuditTrailDescriptor;
    line: EntityAuditTrailEntryDto;
    classifiers: Record<string, ClassifierData | undefined>;
}) {
    const [expanded, setExpanded] = useState(false);
    const dateFormat = useEffectiveDateFormat();
    const change = valueChange(line, classifiers, dateFormat);
    const note = mergeNote(line);
    const truncatable = (value: string) => value.length > VALUE_TRUNCATE_AT && !isUrlValue(value);
    const expandable = change !== null && (truncatable(change.oldValue) || truncatable(change.newValue));
    const show = (value: string) => {
        if (isUrlValue(value)) {
            return (
                <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={value}
                    className="text-link no-underline hover:underline"
                >
                    {t('audit-trail.link')}
                </a>
            );
        }
        return expanded || value.length <= VALUE_TRUNCATE_AT
            ? value
            : value.slice(0, VALUE_TRUNCATE_AT).trimEnd() + '\u2026';
    };

    return (
        <div>
            <div className="text-foreground text-sm">
                {eventSentenceSegments(trail, line, classifiers).map((segment, index) =>
                    segment.value ? (
                        <span key={index} className="font-semibold">
                            {segment.text}
                        </span>
                    ) : (
                        <span key={index}>{segment.text}</span>
                    )
                )}
            </div>
            {change !== null && (
                <div
                    className={cn(
                        'mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-sm',
                        isMonospacedValue(line) && 'font-mono'
                    )}
                >
                    {change.kind !== 'add' && (
                        <span
                            className={cn(
                                'text-muted-foreground break-words',
                                change.oldValue.length <= STRIKETHROUGH_UP_TO &&
                                    !isUrlValue(change.oldValue) &&
                                    'decoration-grey-300 line-through'
                            )}
                        >
                            {show(change.oldValue)}
                        </span>
                    )}
                    {change.kind !== 'remove' && (
                        // The arrow travels with the new value: when a long old value fills the line,
                        // the pair wraps together instead of stranding the arrow on a line of its own.
                        <span className="inline-flex min-w-0 items-baseline gap-1.5">
                            {change.kind === 'update' && (
                                <span className="text-grey-400 flex-none" aria-hidden="true">
                                    &rarr;
                                </span>
                            )}
                            <span className="text-foreground font-semibold break-words">{show(change.newValue)}</span>
                        </span>
                    )}
                    {expandable && (
                        <button
                            type="button"
                            className="text-link cursor-pointer text-xs hover:underline"
                            onClick={() => setExpanded((current) => !current)}
                        >
                            {expanded
                                ? t(`${trail.localeNamespace}.show-less`)
                                : t(`${trail.localeNamespace}.show-more`)}
                        </button>
                    )}
                </div>
            )}
            {note && <div className="text-muted-foreground mt-0.5 text-xs">{note}</div>}
        </div>
    );
}
